/**
 * Sub-nav for a foundation run: Overview | Goal | Target | TFD | ALO | Foundational Symbols.
 * Used inside /dashboard/brand-strategy/foundations/:runId.
 *
 * Mobile (<md): collapses to a native <select> dropdown — no horizontal scrolling.
 * Desktop (md+): full horizontal tab row.
 */

import type { ChangeEvent, FC } from 'react'
import { useNavigate, useParams, useLocation } from 'react-router-dom'
import { subjectFeaturePath, subjectScope } from '@/modules/subjects/scope-navigation'
import { CheckCircle2, Flag, LayoutDashboard, Layers, Target, Lightbulb, Sparkles, ChevronDown, MessageSquare, FileText } from 'lucide-react'

const ITEMS: { id: string; label: string; path: string; icon: typeof Target; isIndex?: boolean }[] = [
    { id: 'overview', label: 'Overview', path: '', icon: LayoutDashboard, isIndex: true },
    { id: 'goal', label: 'Goal', path: '/goal', icon: Flag },
    { id: 'target', label: 'Audience', path: '/target', icon: Target },
    { id: 'tfd', label: 'Mindset', path: '/tfd', icon: Lightbulb },
    { id: 'alo', label: 'Brand Truth', path: '/alo', icon: Layers },
    { id: 'foundational-symbols', label: 'Brand Voice', path: '/foundational-symbols', icon: Sparkles },
    { id: 'message-strategy', label: 'Claims', path: '/message-strategy', icon: Sparkles },
    { id: 'value-props', label: 'Value Props', path: '/value-props', icon: MessageSquare },
    { id: 'communication-briefs', label: 'Briefs', path: '/communication-briefs', icon: FileText },
]

interface FoundationsSubNavProps {
    /** When false, sub-nav scrolls with the page (e.g. inside a parent sticky stack). Default true. */
    sticky?: boolean
}

export const FoundationsSubNav: FC<FoundationsSubNavProps> = ({ sticky = true }) => {
    const navigate = useNavigate()
 const { runId, subjectType, subjectId } = useParams<{ runId: string; subjectType?: string; subjectId?: string }>()
 const location = useLocation()
 const scope = subjectScope(subjectType, subjectId)
 const base = scope ? subjectFeaturePath(scope, 'foundations', runId || '') : `/dashboard/brand-strategy/foundations/${runId || ''}`
    const defineItems = ITEMS.slice(0, 4)
    const shapeItems = ITEMS.slice(4)

    const currentId = (() => {
        const path = location.pathname
        if (path.includes('/communication-briefs')) return 'communication-briefs'
        if (path.endsWith('/value-props')) return 'value-props'
        if (path.endsWith('/message-strategy')) return 'message-strategy'
        if (path.endsWith('/foundational-symbols')) return 'foundational-symbols'
        if (path.endsWith('/alo')) return 'alo'
        if (path.endsWith('/tfd')) return 'tfd'
        if (path.endsWith('/target')) return 'target'
        if (path.endsWith('/goal')) return 'goal'
        if (path === base || path === base + '/') return 'overview'
        return 'overview'
    })()

    if (!runId) return null

    const handleSelectChange = (e: ChangeEvent<HTMLSelectElement>) => {
        const item = ITEMS.find(i => i.id === e.target.value)
        if (item) navigate(item.isIndex ? base : `${base}${item.path}`)
    }

    return (
        <div
            className={`bg-cz-surface-raised border-b border-cz-stroke shadow-sm ${
                sticky ? 'sticky top-[100px] sm:top-[120px] z-20' : 'relative z-0'
            }`}
        >
            <div className='max-w-7xl mx-auto px-4 sm:px-6'>

                {/* Mobile: native select dropdown */}
                <div className='md:hidden py-2'>
                    <div className='relative'>
                        <select
                            value={currentId}
                            onChange={handleSelectChange}
                            className='w-full min-h-[44px] appearance-none rounded-lg border border-cz-stroke bg-cz-surface-muted pl-3 pr-8 py-2 text-sm font-medium text-foreground focus:outline-none focus:ring-2 focus:ring-cz-focus focus:border-cz-focus'
                        >
                            {ITEMS.map(item => (
                                <option key={item.id} value={item.id}>{item.label}</option>
                            ))}
                        </select>
                        <ChevronDown className='pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground' />
                    </div>
                </div>

                {/* Desktop: full horizontal tab row */}
                <nav className='hidden md:flex items-center gap-1.5 py-2'>
                    {[defineItems, shapeItems].map((group, groupIdx) => (
                        <div key={groupIdx} className='flex items-center gap-1.5'>
                            {group.map((item) => {
                                const isActive = currentId === item.id
                                const href = item.isIndex ? base : `${base}${item.path}`
                                const Icon = item.icon
                                return (
                                    <button
                                        key={item.id}
                                        type='button'
                                        onClick={() => navigate(href)}
                                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm font-medium transition-all whitespace-nowrap ${
                                            isActive
                                                ? 'bg-accent text-accent-foreground shadow-sm'
                                                : 'text-muted-foreground hover:bg-cz-surface-muted hover:text-foreground'
                                        }`}
                                    >
                                        <Icon className={`w-4 h-4 ${isActive ? 'text-cz-purple-700 dark:text-cz-purple-500' : ''}`} />
                                        <span>{item.label}</span>
                                        {isActive && <CheckCircle2 className='w-3.5 h-3.5 text-cz-purple-700 dark:text-cz-purple-500' />}
                                    </button>
                                )
                            })}
                            {groupIdx === 0 && <div className='h-6 w-px bg-cz-stroke mx-1' />}
                        </div>
                    ))}
                </nav>

            </div>
        </div>
    )
}
